import React from "react";

const SITE_URL = "https://www.optimagrowth.agency";
const LOGO_URL = `${SITE_URL}/logo.png`;

const safeStringify = (data) =>
  JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");

const JsonLd = ({ data }) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{ __html: safeStringify(data) }}
  />
);

const ORGANIZATION_DATA = {
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "OptimaGrowth",
  url: SITE_URL,
  logo: { "@type": "ImageObject", url: LOGO_URL },
  description: "Growth Partner & Automation Company helping businesses scale through digital marketing, AI automation, and data-driven strategies.",
  email: "info@optimagrowth.agency",
  telephone: "+27661229790",
  address: {
    "@type": "PostalAddress",
    addressCountry: "ZA",
  },
  sameAs: [
    "https://www.linkedin.com/company/optimagrowth",
    "https://www.instagram.com/optimagrowth",
    "https://www.facebook.com/optimagrowth",
  ],
};

export const OrganizationSchema = () => (
  <JsonLd data={{
    "@context": "https://schema.org",
    "@graph": [
      ORGANIZATION_DATA,
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: SITE_URL,
        name: "OptimaGrowth",
        publisher: { "@id": `${SITE_URL}/#organization` },
        potentialAction: {
          "@type": "SearchAction",
          target: { "@type": "EntryPoint", urlTemplate: `${SITE_URL}/blog?q={search_term_string}` },
          "query-input": "required name=search_term_string",
        },
      },
    ],
  }} />
);

export const LocalBusinessSchema = () => (
  <JsonLd data={{
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "OptimaGrowth",
    url: SITE_URL,
    logo: LOGO_URL,
    image: LOGO_URL,
    email: "info@optimagrowth.agency",
    telephone: "+27661229790",
    description: "SEO agency and marketing automation partner in Cape Town, South Africa. We build lead generation systems, CRM automation, SEO strategies, paid media campaigns, and AI-powered growth solutions for businesses in South Africa, UK, and USA.",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Cape Town",
      addressRegion: "Western Cape",
      addressCountry: "ZA",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: -33.9249,
      longitude: 18.4241,
    },
    priceRange: "R5,999 - R45,000/month",
    currenciesAccepted: "ZAR, GBP, USD",
    paymentAccepted: "Bank Transfer, Credit Card",
    openingHours: "Mo-Fr 08:00-17:00",
    sameAs: [
      "https://www.linkedin.com/company/optimagrowth",
      "https://www.instagram.com/optimagrowth",
      "https://www.facebook.com/optimagrowth",
      "https://share.google/tt5TWRSOahMa0OiEN",
    ],
    areaServed: [
      { "@type": "Country", name: "South Africa" },
      { "@type": "Country", name: "United Kingdom" },
      { "@type": "Country", name: "United States" },
      { "@type": "City", name: "Cape Town" },
      { "@type": "City", name: "Pretoria" },
      { "@type": "City", name: "Johannesburg" },
      { "@type": "City", name: "London" },
    ],
    knowsAbout: ["SEO", "Marketing Automation", "Lead Generation", "CRM Automation", "Google Ads", "Meta Ads", "AI Marketing", "Sales Funnels"],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Digital Marketing Services",
      itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "SEO & Content Marketing" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Google & Meta Ads Management" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Marketing Automation" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "CRM & Sales Funnel Management" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "AI Operations & Chatbots" } },
      ],
    },
  }} />
);

export const ServiceSchema = ({ name, description, slug }) => (
  <JsonLd data={{
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url: `${SITE_URL}/services/${slug}`,
    provider: { "@id": `${SITE_URL}/#organization` },
    areaServed: [
      { "@type": "Country", name: "South Africa" },
      { "@type": "Country", name: "United Kingdom" },
      { "@type": "Country", name: "United States" },
    ],
    serviceType: "Digital Marketing",
  }} />
);

export const ArticleSchema = ({ title, description, slug, date, category, tags = [] }) => (
  <JsonLd data={{
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    url: `${SITE_URL}/blog/${slug}`,
    datePublished: date,
    dateModified: date,
    author: { "@type": "Organization", name: "OptimaGrowth" },
    publisher: {
      "@type": "Organization",
      name: "OptimaGrowth",
      logo: { "@type": "ImageObject", url: LOGO_URL },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${slug}` },
    keywords: tags.join(", "),
    articleSection: category,
  }} />
);

export const FAQSchema = ({ faqs }) => (
  <JsonLd data={{
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  }} />
);

export const BreadcrumbSchema = ({ items }) => (
  <JsonLd data={{
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url ? `${SITE_URL}${item.url}` : undefined,
    })),
  }} />
);

export const ServiceListSchema = ({ services }) => (
  <JsonLd data={{
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "OptimaGrowth Services",
    itemListElement: services.map((service, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Service",
        name: service.title,
        description: service.description,
        url: `${SITE_URL}/services/${service.slug}`,
        provider: { "@id": `${SITE_URL}/#organization` },
      },
    })),
  }} />
);
